import { RDKitModule } from "@rdkit/rdkit";
import { useEffect, useState, useRef } from "react";
import { convertNumberToDecimal, getAtomOrBondColors } from "../../../utils/formattingFns";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell, TooltipProps } from 'recharts';
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";

export interface HighlightingBarData {
    char: string, 
    value: number, 
    absValue: number,
    color: string,
    atomIndex: number,
}

const BAR_WIDTH = 16;

const chunkArray = (arr: any[], chunkSize: number) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    chunks.push(arr.slice(i, i + chunkSize));
  }
  return chunks;
};

export default function DomainBarPlot({
    atom_bond_importance,
    smiles,
    absMax,
}: {
    atom_bond_importance: any,
    smiles: string,
    absMax: number,
}) {
    const containerRef = useRef(null);
    const [ barData, setBarData ] = useState<HighlightingBarData[]>([]);
    const [ chunks, setChunks ] = useState<HighlightingBarData[][]>([]);

    useEffect(() => {
        if (!window.RDKit) return;

        const instance = window.RDKit;
        if (smiles && instance) {
            const parsedImportance = atom_bond_importance as { atoms: {[key: number]: number}, bonds: {[key: number]: number} };
            getCharacterAtomMap(instance, smiles, parsedImportance.atoms);
        }
    }, [smiles, atom_bond_importance]);

    useEffect(() => {
        const observer = new ResizeObserver(([entry]) => {
            const width = entry.contentRect.width;
            const barsPerRow = Math.max(Math.floor(width / (BAR_WIDTH+2)), 1);
            const splitData = chunkArray(barData, barsPerRow);
            setChunks(splitData);
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        };

        return () => observer.disconnect();
    }, [barData]);

    function getCharacterAtomMap(RDKit: RDKitModule, inputSmiles: string, atoms: { [key: number]: number }) {
        const mol = RDKit.get_mol(inputSmiles);

        if (mol) {
            const { colors } = getAtomOrBondColors(atoms);

            let removedChars: { char: string, index: number }[] = [];
            let filteredChars: string[] = [];

            const extendedSmiles = mol.get_cxsmiles();
            const charArr = [...extendedSmiles];
            charArr.forEach((char, index) => {
                if ("()=1234567890#-/\\".includes(char)) {
                    removedChars.push({ char, index })
                } else {
                    filteredChars.push(char);
                }
            });

            const charsWithValues: { char: string, value: number, absValue: number, color: string, atomIndex: number }[] = [];

            Object.keys(atoms).forEach(atomKey => {
                const atomValue = atoms[Number(atomKey)];
                const colorKeys = Object.keys(colors).map(color => Number(color));
                const matchingChar = filteredChars.find((c,i) => i === Number(atomKey));
                const matchingColor = colorKeys.find(key => key === Number(atomKey));
                if (matchingChar && matchingColor) {
                    const color = colors[matchingColor];
                    const r = color[0]*255;
                    const g = color[1]*255;
                    const b = color[2]*255;
                    charsWithValues.push({ char: matchingChar, value: atomValue, absValue: Math.abs(atomValue), color: `rgb(${r},${g},${b})`, atomIndex: Number(atomKey) });
                }
            });

            removedChars.forEach(removedChar => {
                const { char, index} = removedChar;
                charsWithValues.splice(index, 0, { char, value: 0, absValue: 0, color: `rgb(255,255,255)`, atomIndex: -1 })
            });

            setBarData(charsWithValues);

        }

        
    }

    const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
        const isVisible = active && payload && payload.length;
        return (
            <div className='flex flex-col p-2 rounded bg-white border border-outline justify-start items-start gap-2' style={{ visibility: isVisible ? 'visible' : 'hidden' }}>
            {isVisible && (
                <>
                <p className="text-[14px]">{payload[0].payload.char}</p>
                <p className="text-[14px] text-default">Value: <span className="text-primary">{convertNumberToDecimal(payload[0].payload.value)}</span></p>
                {payload[0].payload.atomIndex !== -1 && (
                    <p className="text-[14px] text-default">Atom Index: <span className="text-primary">{payload[0].payload.atomIndex}</span></p>
                )}
                </>
            )}
            </div>
        );
    };


    return (
        <div ref={containerRef}>
        {barData.length > 0 && chunks.length > 0 ? (
            <>
            {chunks.map((chunk, rowIndex) => {
                const rowWidth = chunk.length * (BAR_WIDTH+4);
                return (
                    <ResponsiveContainer key={rowIndex} width={rowIndex < chunks.length-1 ? '100%' : rowWidth} height={60}>
                        <BarChart data={chunk}>
                            <XAxis dataKey='char'/>
                            {/* <YAxis /> */}
                            <Tooltip 
                                content={<CustomTooltip />}
                                wrapperStyle={{ zIndex: 1000 }}
                                // formatter={(value, name, props) => {
                                //     const realValue = convertNumberToDecimal(props.payload!.value);
                                //     return [realValue, 'value']
                                // }}
                            />
                            <Bar dataKey='absValue' >
                                {chunk.map((data, index) => (
                                    <Cell key={index} fill={data.color}/>
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )
            })}
            </>
        ) : (
            <></>
        )}
        
        </div>
    )
}